import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Banknote, CheckCircle, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { extractErrorMessage } from './utils.jsx';

const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

/**
 * Mark an invoice paid via offline channel (cash / check / Zelle / Venmo)
 * or by draining the parent's account credit (account_credit).
 *
 * When the admin picks "Account Credit", we fetch the parent's live
 * balance and render the same breakdown the parent sees on the Pay-With-
 * Credit dialog: Available · Fee · Will apply · Remaining due — plus
 * full/partial/no-credit messaging.
 */
export default function MarkPaidDialog({ open, onOpenChange, invoice, onMarked }) {
  const [form, setForm] = useState({ method: 'cash', amount: '', reference_number: '', notes: '' });
  const [processing, setProcessing] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      // iter270: for PARTIAL invoices the chargeable amount is the
      // outstanding `balance_due`, not the full invoice amount (parent
      // already paid the rest via account credit).
      const effective = invoice.status === 'partial' && invoice.balance_due != null
        ? Number(invoice.balance_due)
        : Number(invoice.amount || 0);
      setForm({
        method: 'cash',
        amount: effective ? effective.toString() : '',
        reference_number: '',
        notes: '',
      });
      setCreditBalance(null);
    }
  }, [open, invoice]);

  // When admin flips to account_credit, fetch the parent's live balance.
  useEffect(() => {
    if (!open || !invoice) return;
    if (form.method !== 'account_credit') return;
    const parentId = invoice.parent_id;
    if (!parentId) return;
    let cancelled = false;
    (async () => {
      setCreditLoading(true);
      try {
        const { data } = await api.get(`/parents/${parentId}/credit-balance`);
        if (cancelled) return;
        setCreditBalance(
          Number(data?.customer_credit_balance || 0)
          + Number(data?.credit_balance || 0),
        );
      } catch {
        if (!cancelled) setCreditBalance(0);
      } finally {
        if (!cancelled) setCreditLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.method, invoice]);

  const isCreditMethod = form.method === 'account_credit';
  // PR 19y4 — the real outstanding balance on this invoice.
  const invoiceAmountDue = Number(
    invoice?.status === 'partial' && invoice?.balance_due != null
      ? invoice.balance_due
      : invoice?.amount || 0
  );
  // iter389 — For Account Credit the editable "Amount Received" box is
  // irrelevant and was misleading admins (it showed the full $165 while
  // only $80 of credit exists). Credit always applies toward the full
  // invoice balance, capped at available credit.
  const fee = isCreditMethod
    ? invoiceAmountDue
    : (parseFloat(form.amount) || invoice?.amount || 0);
  const liveBal = Number(creditBalance ?? 0);
  const willApply = Math.min(liveBal, fee);
  const remainingDue = Math.max(0, +(fee - willApply).toFixed(2));
  const noCredit = isCreditMethod && creditBalance !== null && liveBal <= 0;
  const isPartial = isCreditMethod && willApply > 0 && remainingDue > 0;

  const enteredAmount = parseFloat(form.amount) || 0;
  const overage = !isCreditMethod && enteredAmount > invoiceAmountDue + 0.005
    ? +(enteredAmount - invoiceAmountDue).toFixed(2)
    : 0;
  const [overageAction, setOverageAction] = useState('credit'); // 'credit' | 'ignore'
  useEffect(() => {
    // Reset choice when overage clears (e.g. admin lowers amount).
    if (!overage) setOverageAction('credit');
  }, [overage]);

  const handleSubmit = async () => {
    if (!invoice) return;
    setProcessing(true);
    try {
      // PR 19y4 — For offline overage:
      //   - `amount` sent to backend is CAPPED at invoice_amount_due
      //     (payment record reflects only what was applied to the
      //      invoice)
      //   - `overage_credit_amount` carries the excess if admin chose
      //     to credit it; else 0 (admin gave the excess back physically)
      // For Account Credit, only ever charge what credit actually exists
      // (`willApply`). Sending the full invoice amount when credit is short
      // made the backend reject with "Available credit is less than $X".
      // The backend marks the invoice PARTIAL off the real invoice total.
      const effectiveAmount = isCreditMethod
        ? willApply
        : (overage > 0 ? invoiceAmountDue : fee);
      const overageCreditAmount = overage > 0 && overageAction === 'credit' ? overage : 0;
      const { data: resp } = await api.post('/admin/invoices/mark-paid', {
        invoice_id: invoice.id,
        invoice_type: invoice.type,
        payment_method: form.method,
        amount: effectiveAmount,
        reference_number: form.reference_number || null,
        notes: form.notes || null,
        // iter307df — Forward the specific MI id when the parent
        // invoice list row exposes it. Backend uses this to flip the
        // exact monthly_invoice row admin clicked on (instead of the
        // NBD-cursor MI, which is wrong for backdated manual cycles).
        monthly_invoice_id: invoice.monthly_invoice_id || null,
        overage_credit_amount: overageCreditAmount,
      });
      const credited = Number(resp?.overage_credited || 0);
      if (credited > 0) {
        toast.success(
          `Invoice marked paid — ${fmt(credited)} overage deposited to account credit`
        );
      } else {
        toast.success(
          isCreditMethod && isPartial
            ? `${fmt(willApply)} credit applied — ${fmt(remainingDue)} still due`
            : 'Invoice marked as paid'
        );
      }
      onOpenChange(false);
      onMarked && onMarked();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to mark as paid'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-green-600" />
            Record Payment
          </DialogTitle>
          <DialogDescription>
            Mark this invoice paid for an offline payment (cash, check, Zelle, Venmo)
            or drain the parent&apos;s account credit.
          </DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <p><strong>Invoice:</strong> {invoice.description}</p>
              <p><strong>Parent:</strong> {invoice.parent_name}</p>
              <p className="text-lg font-semibold text-green-600">
                <strong>Amount Due:</strong> {fmt(
                  invoice.status === 'partial' && invoice.balance_due != null
                    ? invoice.balance_due
                    : invoice.amount,
                )}
                {invoice.status === 'partial' && (
                  <span className="block text-xs font-normal text-amber-700 mt-0.5">
                    (Invoice total {fmt(invoice.amount)} · credit applied {fmt(invoice.credit_applied)})
                  </span>
                )}
              </p>
            </div>

            <div>
              <Label>Payment Method *</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="mark-paid-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="ach" data-testid="mark-paid-method-ach">ACH / Bank Transfer</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="account_credit" data-testid="mark-paid-method-credit">Account Credit</SelectItem>
                  <SelectItem value="other" data-testid="mark-paid-method-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isCreditMethod && (
              <div className="rounded-md border bg-purple-50/40 border-purple-200 p-3 text-sm space-y-1.5" data-testid="mark-paid-credit-breakdown">
                <div className="flex items-center gap-1.5 font-medium text-purple-900">
                  <Sparkles className="h-4 w-4" /> Account Credit
                </div>
                {creditLoading ? (
                  <div className="text-xs text-purple-700 flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading balance…
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <span className="text-slate-600">Available balance</span>
                      <span className="font-mono text-right font-medium" data-testid="mp-credit-available">{fmt(liveBal)}</span>
                      <span className="text-slate-600">Invoice amount</span>
                      <span className="font-mono text-right">{fmt(fee)}</span>
                      <span className="text-slate-600 font-medium">Will apply</span>
                      <span className="font-mono text-right text-purple-900 font-semibold" data-testid="mp-credit-will-apply">{fmt(willApply)}</span>
                      <span className={`font-medium ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>Remaining due</span>
                      <span className={`font-mono text-right font-semibold ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="mp-credit-remaining">{fmt(remainingDue)}</span>
                    </div>
                    {noCredit && (
                      <div className="flex items-start gap-1.5 text-xs text-rose-700 mt-1">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>Parent has no credit available. Pick another method.</span>
                      </div>
                    )}
                    {isPartial && !noCredit && (
                      <div className="flex items-start gap-1.5 text-xs text-amber-800 mt-1">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          Credit won&apos;t cover the full amount. <strong>{fmt(willApply)}</strong> will drain from credit; this invoice keeps a <strong>{fmt(remainingDue)}</strong> balance due (collect via another method later).
                        </span>
                      </div>
                    )}
                    {!isPartial && !noCredit && (
                      <div className="text-xs text-emerald-700 mt-1">
                        Credit covers the full amount — invoice will be marked Paid.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!isCreditMethod && (
            <div>
              <Label>Amount Received</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={(
                  invoice.status === 'partial' && invoice.balance_due != null
                    ? Number(invoice.balance_due)
                    : Number(invoice.amount || 0)
                ).toFixed(2)}
                data-testid="mark-paid-amount"
              />
            </div>
            )}

            {overage > 0 && (
              <div
                className="rounded-md border bg-amber-50/60 border-amber-300 p-3 text-sm space-y-2"
                data-testid="mark-paid-overage-alert"
              >
                <div className="flex items-start gap-1.5 text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Overpayment detected</div>
                    <div className="text-xs">
                      You entered <strong>{fmt(enteredAmount)}</strong> but the invoice is
                      only <strong>{fmt(invoiceAmountDue)}</strong> &mdash; that&apos;s{' '}
                      <strong>{fmt(overage)}</strong> extra.
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5 pl-6">
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="overage_action"
                      value="credit"
                      checked={overageAction === 'credit'}
                      onChange={() => setOverageAction('credit')}
                      className="mt-0.5"
                      data-testid="mark-paid-overage-credit"
                    />
                    <span>
                      <strong>Deposit {fmt(overage)} to parent&apos;s account credit</strong>
                      <span className="block text-slate-600">
                        Invoice paid at {fmt(invoiceAmountDue)}; overage recorded as a
                        credit ledger entry for future use.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="overage_action"
                      value="ignore"
                      checked={overageAction === 'ignore'}
                      onChange={() => setOverageAction('ignore')}
                      className="mt-0.5"
                      data-testid="mark-paid-overage-ignore"
                    />
                    <span>
                      <strong>Only record {fmt(invoiceAmountDue)} (invoice amount)</strong>
                      <span className="block text-slate-600">
                        {fmt(overage)} not recorded anywhere — you handed the extra back
                        to the parent physically.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {!isCreditMethod && (
              <>
                <div>
                  <Label>Reference Number (optional)</Label>
                  <Input
                    value={form.reference_number}
                    onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                    placeholder="Check #, Zelle confirmation, etc."
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={processing || (isCreditMethod && (noCredit || creditLoading))}
            className="bg-green-600 hover:bg-green-700"
            data-testid="mark-paid-confirm"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
            ) : isCreditMethod ? (
              <><Sparkles className="h-4 w-4 mr-2" />
                {isPartial ? `Apply ${fmt(willApply)} (Partial)` : `Apply ${fmt(willApply)}`}
              </>
            ) : overage > 0 && overageAction === 'credit' ? (
              <><CheckCircle className="h-4 w-4 mr-2" />
                Mark {fmt(invoiceAmountDue)} paid + {fmt(overage)} credit
              </>
            ) : overage > 0 && overageAction === 'ignore' ? (
              <><CheckCircle className="h-4 w-4 mr-2" />
                Mark {fmt(invoiceAmountDue)} paid
              </>
            ) : (
              <><CheckCircle className="h-4 w-4 mr-2" />Mark as Paid</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
